import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  TotpMultiFactorGenerator,
  type MultiFactorResolver,
} from "firebase/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/mfa-verify")({
  component: MfaVerifyPage,
});

function MfaVerifyPage() {
  const navigate = useNavigate();
  const resolver = (window as unknown as { __mfaResolver?: MultiFactorResolver }).__mfaResolver;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function verify() {
    if (!resolver) {
      setError("No active MFA session — please sign in again.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const factor = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
      if (!factor) throw new Error("No TOTP factor enrolled.");
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(factor.uid, code);
      await resolver.resolveSignIn(assertion);
      delete (window as unknown as { __mfaResolver?: unknown }).__mfaResolver;
      navigate({ to: "/dashboard" });
    } catch (err) {
      setError((err as Error).message ?? "Verification failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6 rounded-xl border bg-card p-8 shadow-sm">
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-semibold">Two-factor verification</h1>
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>
        <div className="space-y-2">
          <label htmlFor="code" className="text-sm font-medium">
            6-digit code
          </label>
          <Input
            id="code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <Button onClick={() => void verify()} disabled={busy || code.length !== 6} className="w-full">
          {busy ? "Verifying…" : "Verify"}
        </Button>
      </div>
    </div>
  );
}
